// src/components/BCP/ReviewStep.tsx - Enhanced with detailed function display
import React from 'react';
import { ReviewStepProps, CriticalFunction } from '../../types/bcpForm';
import { AnalysisStruct } from '../../services/api';

const ReviewStep: React.FC<ReviewStepProps> = ({ additionalLocations, analysis, formData }) => {
  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCriticalityStats = () => {
    const stats = formData.critical_functions.reduce(
      (acc, func) => {
        if (typeof func === 'object' && func.criticality) {
          acc[func.criticality] = (acc[func.criticality] || 0) + 1;
        } else {
          acc.unspecified = (acc.unspecified || 0) + 1;
        }
        return acc;
      },
      { critical: 0, high: 0, medium: 0, low: 0, unspecified: 0 }
    );
    return stats;
  };

  const formatRTO = (rto: string) => {
    const rtoMap: { [key: string]: string } = {
      'immediate': 'Immediate (< 1 hour)',
      '1-4-hours': '1-4 hours',
      '4-24-hours': '4-24 hours',
      '1-3-days': '1-3 days',
      'more-than-3-days': '> 3 days'
    };
    return rtoMap[rto] || rto;
  };

  const formatRPO = (rpo: string) => {
    const rpoMap: { [key: string]: string } = {
      'none': 'No data loss acceptable',
      '1-hour': '≤ 1 hour',
      '4-hours': '≤ 4 hours',
      '24-hours': '≤ 24 hours',
      'more-than-24-hours': '> 24 hours'
    };
    return rpoMap[rpo] || rpo;
  };

  const stats = getCriticalityStats();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Review & Generate</h2>
        <p className="text-gray-600">Review your information and generate your BCP</p>
      </div>

      {/* Business Summary */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Business Summary</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="text-sm font-medium text-gray-500">Business Name</div>
            <div className="text-base text-gray-900">{formData.business_name}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Industry</div>
            <div className="text-base text-gray-900">{formData.industry}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Business Type</div>
            <div className="text-base text-gray-900">{formData.business_type}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Employees</div>
            <div className="text-base text-gray-900">{formData.employee_count}</div>
          </div>
        </div>
      </div>

      {/* Critical Functions Summary */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Critical Functions Overview</h3>
          <div className="text-sm text-gray-500">
            {formData.critical_functions.length} total functions
          </div>
        </div>

        {/* Criticality Distribution */}
        <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-5">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            <div className="text-sm text-gray-600">Critical</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.high}</div>
            <div className="text-sm text-gray-600">High</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.medium}</div>
            <div className="text-sm text-gray-600">Medium</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.low}</div>
            <div className="text-sm text-gray-600">Low</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{stats.unspecified}</div>
            <div className="text-sm text-gray-600">Basic</div>
          </div>
        </div>

        {/* Detailed Functions List */}
        <div className="space-y-3">
          {formData.critical_functions.map((func, index) => {
            if (typeof func === 'object') {
              const criticalFunction = func as CriticalFunction;
              return (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-semibold text-gray-900">{criticalFunction.name}</div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticalityColor(criticalFunction.criticality)}`}>
                      {criticalFunction.criticality.charAt(0).toUpperCase() + criticalFunction.criticality.slice(1)}
                    </span>
                  </div>

                  {criticalFunction.description && (
                    <div className="mb-3 text-sm text-gray-600">{criticalFunction.description}</div>
                  )}

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">RTO</div>
                      <div className="text-sm text-gray-900">{formatRTO(criticalFunction.rto)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">RPO</div>
                      <div className="text-sm text-gray-900">{formatRPO(criticalFunction.rpo)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">Owner</div>
                      <div className="text-sm text-gray-900">{criticalFunction.owner || 'Not specified'}</div>
                    </div>
                  </div>

                  {criticalFunction.dependencies && criticalFunction.dependencies.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Dependencies</div>
                      <div className="flex flex-wrap gap-1">
                        {criticalFunction.dependencies.map((dep, depIndex) => (
                          <span key={depIndex} className="px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-md border border-blue-200">
                            {dep}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {criticalFunction.resources && criticalFunction.resources.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Required Resources</div>
                      <div className="flex flex-wrap gap-1">
                        {criticalFunction.resources.map((resource, resIndex) => (
                          <span key={resIndex} className="px-2 py-1 text-xs text-purple-800 bg-purple-100 rounded-md border border-purple-200">
                            {resource}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            } else {
              // Handle legacy string format
              return (
                <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-gray-900">{func as string}</div>
                    <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-full border border-gray-300">
                      Basic
                    </span>
                  </div>
                </div>
              );
            }
          })}
        </div>
      </div>

      {/* Additional Locations */}
      {additionalLocations.length > 0 && (
        <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h3 className="mb-3 text-lg font-semibold text-gray-900">Additional Locations</h3>
          <div className="space-y-3">
            {additionalLocations.map((loc, idx) => (
              <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{loc.name || `Location #${idx + 1}`}</div>
                    <div className="mt-0.5 text-xs text-gray-700">{loc.contact || '—'}</div>
                    <div className="mt-1 text-sm text-gray-700 whitespace-pre-line">{loc.address || '—'}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      {loc.state || '—'} {loc.zip || ''}
                    </div>
                  </div>
                  <div className="text-sm text-gray-700">{loc.phone || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && <AnalysisResults analysis={analysis} />}

      {/* Ready to Generate */}
      <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
        <div className="flex items-center mb-4 space-x-3">
          <div className="flex justify-center items-center w-8 h-8 bg-green-600 rounded-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Ready to Generate</h3>
        </div>
        <p className="mb-4 text-sm text-gray-700">
          Your BCP will include:
        </p>
        <ul className="space-y-1 text-sm text-gray-700">
          <li className="flex items-center">
            <svg className="mr-2 w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Risk assessment and threat analysis
          </li>
          <li className="flex items-center">
            <svg className="mr-2 w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Business impact analysis with RTO/RPO targets
          </li>
          <li className="flex items-center">
            <svg className="mr-2 w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Recovery strategies and procedures
          </li>
          <li className="flex items-center">
            <svg className="mr-2 w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            ISO 22301 compliance documentation
          </li>
        </ul>
      </div>
    </div>
  );
};

// Enhanced Analysis Results Component
const AnalysisResults: React.FC<{ analysis: AnalysisStruct }> = ({ analysis }) => (
  <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
    <h3 className="mb-4 text-lg font-semibold text-gray-900">AI Business Analysis Results</h3>

    {/* Primary Analysis Grid */}
    <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2">
      {/* Critical Risks */}
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-center mb-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h4 className="ml-2 text-sm font-semibold text-red-900">Critical Risks</h4>
        </div>
        <ul className="space-y-2 text-sm text-red-800">
          {analysis.risks.map((risk, i) => (
            <li key={i} className="flex items-start">
              <div className="flex-shrink-0 w-1.5 h-1.5 bg-red-400 rounded-full mt-2 mr-2"></div>
              {risk}
            </li>
          ))}
        </ul>
      </div>

      {/* Business Impacts */}
      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
        <div className="flex items-center mb-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
            </svg>
          </div>
          <h4 className="ml-2 text-sm font-semibold text-orange-900">Potential Impacts</h4>
        </div>
        <ul className="space-y-2 text-sm text-orange-800">
          {analysis.impacts.map((impact, i) => (
            <li key={i} className="flex items-start">
              <div className="flex-shrink-0 w-1.5 h-1.5 bg-orange-400 rounded-full mt-2 mr-2"></div>
              {impact}
            </li>
          ))}
        </ul>
      </div>

      {/* Mitigation Strategies */}
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center mb-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h4 className="ml-2 text-sm font-semibold text-green-900">Recommended Actions</h4>
        </div>
        <ul className="space-y-2 text-sm text-green-800">
          {analysis.mitigations.map((mitigation, i) => (
            <li key={i} className="flex items-start">
              <svg className="flex-shrink-0 mt-1 mr-2 w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {mitigation}
            </li>
          ))}
        </ul>
      </div>

      {/* Missing Information */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center mb-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="ml-2 text-sm font-semibold text-blue-900">Additional Info Needed</h4>
        </div>
        <ul className="space-y-2 text-sm text-blue-800">
          {analysis.missing_info.map((item, i) => (
            <li key={i} className="flex items-start">
              <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-2"></div>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Additional Analysis Sections */}
    {(analysis.industry_insights && analysis.industry_insights.length > 0) && (
      <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2">
        {/* Industry Insights */}
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h4 className="ml-2 text-sm font-semibold text-purple-900">Industry Insights</h4>
          </div>
          <ul className="space-y-2 text-sm text-purple-800">
            {analysis.industry_insights.map((insight, i) => (
              <li key={i} className="flex items-start">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 mr-2"></div>
                {insight}
              </li>
            ))}
          </ul>
        </div>

        {/* Compliance Notes */}
        {analysis.compliance_notes && analysis.compliance_notes.length > 0 && (
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center mb-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h4 className="ml-2 text-sm font-semibold text-indigo-900">Compliance Requirements</h4>
            </div>
            <ul className="space-y-2 text-sm text-indigo-800">
              {analysis.compliance_notes.map((note, i) => (
                <li key={i} className="flex items-start">
                  <div className="flex-shrink-0 w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 mr-2"></div>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}

    {/* Recovery Priorities */}
    {analysis.recovery_priorities && analysis.recovery_priorities.length > 0 && (
      <div className="p-4 mb-6 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex items-center mb-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h4 className="ml-2 text-sm font-semibold text-amber-900">Recovery Priority Sequence</h4>
        </div>
        <div className="space-y-3">
          {analysis.recovery_priorities.map((priority, i) => (
            <div key={i} className="flex items-start">
              <div className="flex flex-shrink-0 justify-center items-center mr-3 w-6 h-6 text-xs font-bold text-amber-800 bg-amber-200 rounded-full">
                {i + 1}
              </div>
              <span className="text-sm text-amber-800">{priority}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Analysis Summary */}
    <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
      <div className="flex items-center mb-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h4 className="ml-2 text-sm font-semibold text-purple-900">AI Business Insights Summary</h4>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="text-center">
          <div className="text-xl font-bold text-purple-600">
            {analysis.risks.length}
          </div>
          <div className="text-xs text-purple-800">Critical Risks</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-purple-600">
            {analysis.mitigations.length}
          </div>
          <div className="text-xs text-purple-800">Strategies</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-purple-600">
            {analysis.recovery_priorities?.length || 0}
          </div>
          <div className="text-xs text-purple-800">Recovery Steps</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-purple-600">
            {Math.max(0, 100 - (analysis.missing_info.length * 10))}%
          </div>
          <div className="text-xs text-purple-800">Complete</div>
        </div>
      </div>
    </div>
  </div>
);

export default ReviewStep;